"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, PhoneCall, ArrowRight, CircleCheck as CheckCircle2, MapPin, Sparkles, ShieldCheck, Clock, Search } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqs = [{
  id: "faq-1",
  question: "Why should my Doncaster business focus on local SEO instead of general national search?",
  answer: "Local SEO focuses on capturing high-intent customers right in Doncaster and surrounding South Yorkshire areas (such as Bessacarr, Armthorpe, Bentley, and Wheatley) who are actively looking for your specific services right now. Rather than competing nationally against huge corporate budgets, I position your business directly in Google's Map Pack (3-Pack) and top local organic results where converting foot traffic and phone calls actually happen."
}, {
  id: "faq-2",
  question: "How quickly can I expect to see local search results in Doncaster?",
  answer: "Initial quick wins—such as Google Business Profile enhancements, local map pin fixes, and technical keyword corrections—often produce visible ranking improvements within 4 to 8 weeks. Competing for competitive local search terms in Doncaster typically achieves dominant, stable rankings within 3 to 6 months of consistent strategy."
}, {
  id: "faq-3",
  question: "Will I work directly with you, Yassine, or will my account be passed to a junior?",
  answer: "You will work 100% directly with me, Yassine Essaadi. I do not operate as an agency with account managers, outsourced interns, or junior account executives. When you book a consultation or review a report, you speak directly with the senior specialist responsible for your search growth."
}, {
  id: "faq-4",
  question: "How do you help my business get into the Google 3-Pack / Map Pack for Doncaster?",
  answer: "Getting into Google's Map Pack requires a precise combination of optimized Google Business Profile settings, localized structured data, accurate UK NAP (Name, Address, Phone) citations, geo-targeted content pages, and authentic local review strategies tailored to South Yorkshire."
}, {
  id: "faq-5",
  question: "Do you tie Doncaster clients into long-term binding contracts?",
  answer: "No. I believe in earning your business through measurable results rather than restrictive 12-month lock-in contracts. I offer flexible monthly agreements backed by clear deliverables, transparent rank tracking, and honest performance reviews."
}, {
  id: "faq-6",
  question: "What makes your approach different from local Doncaster marketing agencies?",
  answer: "Most local agencies use rigid, high-volume templates and delegate tasks across multiple staff members who do not know your business deeply. As an independent consultant, I limit my active client intake to build fully customized, hands-on strategies. I give your business my direct personal focus."
}, {
  id: "faq-7",
  question: "Can you optimize my existing Google Business Profile or do we need to build one from scratch?",
  answer: "In almost all cases, we optimize and upgrade your existing profile. I perform a deep audit of your primary and secondary categories, business descriptions, photo geotagging, services list, Q&A sections, and citation consistency to eliminate suppression factors and maximize local visibility."
}, {
  id: "faq-8",
  question: "How do you target specific areas around Doncaster like Rossington, Edenthorpe, or Thorne?",
  answer: "We build dedicated, hyper-relevant service-area pages and structured schemas for key Doncaster suburbs and nearby towns. This signals to search engines that your business actively serves those communities without resorting to spammy duplicate content."
}, {
  id: "faq-9",
  question: "How important are Google reviews for ranking higher in Doncaster local searches?",
  answer: "Google reviews are one of the most critical local ranking signals and heavily influence customer conversion. I assist you in establishing an automated, natural review generation process that encourages satisfied Doncaster customers to leave detailed, keyword-rich local reviews."
}, {
  id: "faq-10",
  question: "How do you track and report the return on investment (ROI) of local SEO?",
  answer: "I provide clear, fluff-free monthly video walk-throughs and live dashboards tracking actual conversions: inbound call volume, form submissions, direction requests, organic traffic quality, and local Google Map Pack rankings across Doncaster postal codes."
}, {
  id: "faq-11",
  question: "Will AI Search (AI Overviews) impact my local business in Doncaster?",
  answer: "Yes, Google's AI Search Overviews are increasingly answering local queries directly. My local SEO strategies integrate AI Search Optimisation—ensuring your business entity, local authority signals, and structured data are properly referenced in generative search answers."
}, {
  id: "faq-12",
  question: "What is the first step to starting a Doncaster local SEO campaign with you?",
  answer: "The first step is a free, no-obligation Local SEO Consultation. I review your current website, local profile, and Doncaster competitors, then present a honest, actionable roadmap outlining exactly how we can increase your local leads."
}];
export default function Doncasterlocalseofaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="doncasterlocalseofaq" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-secondary/30 rounded-full blur-3xl pointer-events-none -z-10" />
      <div className="absolute bottom-10 left-10 w-80 h-80 bg-accent/10 rounded-full blur-3xl pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <Badge variant="secondary" className="px-3.5 py-1 text-xs font-semibold uppercase tracking-wider gap-1.5 border-secondary/50">
            <MapPin className="size-3.5 text-primary" />
            Doncaster Local SEO Expertise
          </Badge>

          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-default">
            Frequently Asked Questions About <span className="text-primary">Local SEO in Doncaster</span>
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
            Clear, straightforward answers about how I help Doncaster and South Yorkshire businesses dominate local Google searches, rank in the Map Pack, and win more qualified customers.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.1
        }} className="lg:col-span-8">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, i) => <motion.div key={faq.id} variants={itemVariants} data-index={i}>
                  <AccordionItem value={faq.id} className="border border-border/80 rounded-xl bg-card px-5 py-1 shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                    <AccordionTrigger className="text-left font-semibold text-base sm:text-lg text-primary hover:no-underline py-4">
                      <span className="flex items-start gap-3">
                        <HelpCircle className="size-5 text-accent shrink-0 mt-0.5" />
                        <span>{faq.question}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed text-sm sm:text-base pt-1 pb-5 pl-8 font-default">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>)}
            </Accordion>
          </motion.div>

          <div className="lg:col-span-4 lg:sticky lg:top-24 space-y-6">
            <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-md relative overflow-hidden">
              <div className="absolute top-0 right-0 w-28 h-28 bg-primary/5 rounded-bl-full pointer-events-none" />
              
              <div className="flex items-center gap-3 mb-6">
                <div className="size-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
                  YE
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-base">Yassine Essaadi</h3>
                  <p className="text-xs text-muted-foreground">Independent SEO Consultant</p>
                </div>
              </div>

              <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20 mb-4 px-2.5 py-0.5">
                Direct Consultant Access
              </Badge>

              <h4 className="text-xl font-bold text-foreground mb-3 font-default">
                Have a specific question about your Doncaster business?
              </h4>

              <p className="text-sm text-muted-foreground leading-relaxed mb-6 font-default">
                Skip the generic agency pitch. Speak directly with me to discuss your local search goals, current rankings, and target markets across South Yorkshire.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2.5 text-xs text-foreground font-medium">
                  <CheckCircle2 className="size-4 text-emerald-600 shrink-0" />
                  <span>No account managers or interns</span>
                </div>
                <div className="flex items-center gap-2.5 text-xs text-foreground font-medium">
                  <CheckCircle2 className="size-4 text-emerald-600 shrink-0" />
                  <span>Tailored South Yorkshire search roadmap</span>
                </div>
                <div className="flex items-center gap-2.5 text-xs text-foreground font-medium">
                  <CheckCircle2 className="size-4 text-emerald-600 shrink-0" />
                  <span>Transparent reporting &amp; deliverables</span>
                </div>
              </div>

              <div className="space-y-3">
                <Button asChild size="lg" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium shadow-sm">
                  <Link to="/ContactPage">
                    Book a Free SEO Consultation
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="w-full border-border hover:bg-muted">
                  <a href="tel:+447490984430" className="flex items-center justify-center gap-2">
                    <PhoneCall className="size-4 text-primary" />
                    <span>+44 7490 984430</span>
                  </a>
                </Button>
              </div>

              <p className="text-xs text-center text-muted-foreground mt-4">
                Or email me directly at{" "}
                <a href="mailto:contact@ye-consultancy.com" className="text-primary underline hover:text-primary/80">
                  contact@ye-consultancy.com
                </a>
              </p>
            </div>

            <div className="bg-muted/60 border border-border/80 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="size-5 text-accent shrink-0" />
                <span className="text-xs font-medium text-foreground">
                  Ready to optimize for AI Search &amp; Google Map Pack?
                </span>
              </div>
              <Button asChild variant="ghost" size="sm" className="text-xs text-primary hover:text-primary p-0">
                <Link to="/LocalSEOPage">
                  View Local SEO
                </Link>
              </Button>
            </div>
          </div>

        </div>

      </div>
    </section>;
}